import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import Chart from "chart.js/auto";
import { ChartConfiguration, ChartDataset } from "chart.js";

import { Widget } from "../../../dashboard/models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";

import { SharedModule } from "../../../../shared/shared.module";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { TiersFacadeService } from "../../services/tiers/tiers-facade.service";
import { SelectAutocompleteComponent } from "../../../../shared/modules/select-autocomplete/select-autocomplete.component";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { MatIcon } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { CaParPeriodeDialogComponent } from "./ca-par-periode-dialog.component";

export type CaPeriodeScale = "DAY" | "WEEK" | "MONTH";

export interface CaPeriodeBucket {
  label: string;
  d: Date;
  ca: number;
  costMat: number;
  costMO: number;
  cost: number;
  margin: number;
  marginPct: number;
}

@Component({
  selector: "vl-widget-ca-par-periode-tiers",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    PopoverModule,
    WidgetTitleComponent,
    ChipsComponent,
    ChipsToggleComponent,
    SelectAutocompleteComponent,
    MatIcon,
    MatMenuModule,
  ],
  host: { class: "widget-component" },
  styleUrls: ["./ca-par-periode-tiers.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./ca-par-periode-tiers.component.html",
})
export class CaParPeriodeTiersComponent implements OnDestroy {
  private readonly tiersFacade = inject(TiersFacadeService);
  private readonly dialogsService = inject(DialogsService);
  articleInfosFacade = inject(ArticlesInfosFacadeService);

  stats = this.tiersFacade.getState().stats;
  widget = input<Widget>();
  metadata = input<EntityMetadata>();

  selectedArticleId = model<string>(null);

  articlesOptions = computed<{ name: string; value: string }[]>(() => {
    return (
      this.stats()?.tiers?.articlesFournis?.map((av) => ({
        name:
          this.articleInfosFacade.getByArticleId(av.articleId)?.()
            ?.denomination ?? av.articleId,
        value: av.articleId,
      })) ?? []
    );
  });

  private readonly elRef = inject(ElementRef<HTMLElement>);
  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("c");
  chart?: Chart<"bar", number[], string>;

  timeScale = signal<CaPeriodeScale>("MONTH");
  timeScaleChoices = [
    { name: "Jour", value: "DAY" as CaPeriodeScale },
    { name: "Semaine", value: "WEEK" as CaPeriodeScale },
    { name: "Mois", value: "MONTH" as CaPeriodeScale },
  ];

  private fmtEUR2 = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  });
  private fmtPct1 = new Intl.NumberFormat("fr-FR", {
    style: "percent",
    maximumFractionDigits: 1,
  });

  /** Map lotId → coûts profonds (mat, mo). Source unique de vérité. */
  private lotCostMap = computed(() => {
    const m = new Map<string, { mat: number; mo: number }>();
    for (const lc of this.stats()?.lotCosts ?? []) {
      m.set(lc.lotId, {
        mat: parseNumber(lc.coutMat),
        mo: parseNumber(lc.coutMO),
      });
    }
    return m;
  });

  /**
   * Buckets temporels : CA, coûts profonds (mat + MO), marge, taux de marge.
   */
  buckets = computed<CaPeriodeBucket[]>(() => {
    const lignes = (this.stats()?.facturesLignes ?? []) as any[];
    const scale = this.timeScale();
    const costMap = this.lotCostMap();
    const articleId = this.selectedArticleId();

    const filtered = articleId
      ? lignes.filter(
          (fl) =>
            String(fl?.commandeClientLigne?.articleId) === String(articleId),
        )
      : lignes;

    const byKey = new Map<
      string,
      {
        d: Date;
        label: string;
        ca: number;
        costMat: number;
        costMO: number;
      }
    >();

    let minD: Date | null = null;
    let maxD: Date | null = null;

    for (const fl of filtered) {
      const f = fl?.facture ?? fl?.f;
      const dRaw = parseDate(f?.date ?? f?.dateString ?? fl?.createdAt);
      if (!dRaw) continue;

      const d = startOf(dRaw, scale);
      const key = makeKey(d, scale);
      if (!minD || d < minD) minD = d;
      if (!maxD || d > maxD) maxD = d;

      const coefUG =
        parseNumber(fl?.commandeClientLigne?.coefficientUniteGestion ?? 1) || 1;
      const qtyUV = parseNumber(fl?.quantite ?? 0);
      const puVenteUV = parseNumber(fl?.prix ?? 0);
      const ca = qtyUV * puVenteUV;

      const lotId: string | undefined = fl?.lotId ?? fl?.lot?.id;
      const lotCosts = lotId ? costMap.get(lotId) : null;
      const qtyUG = qtyUV * coefUG;
      const costMat = qtyUG * (lotCosts?.mat ?? 0);
      const costMO = qtyUG * (lotCosts?.mo ?? 0);

      const prev = byKey.get(key);
      if (prev) {
        prev.ca += ca;
        prev.costMat += costMat;
        prev.costMO += costMO;
      } else {
        byKey.set(key, {
          d,
          label: formatKey(d, scale),
          ca,
          costMat,
          costMO,
        });
      }
    }

    if (!byKey.size) return [];

    const timeline: Date[] = [];
    for (let dd = new Date(minD!); dd <= maxD!; dd = addOne(dd, scale)) {
      timeline.push(startOf(dd, scale));
    }

    return timeline.map((d) => {
      const key = makeKey(d, scale);
      const b = byKey.get(key);
      const ca = b?.ca ?? 0;
      const costMat = b?.costMat ?? 0;
      const costMO = b?.costMO ?? 0;
      const cost = costMat + costMO;
      const margin = ca - cost;
      const marginPct = ca !== 0 ? margin / ca : 0;
      return {
        label: formatKey(d, scale),
        d,
        ca,
        costMat,
        costMO,
        cost,
        margin,
        marginPct,
      };
    });
  });

  labels = computed(() => this.buckets().map((b) => b.label));
  private seriesCA = computed(() => this.buckets().map((b) => round2(b.ca)));
  private seriesCost = computed(() =>
    this.buckets().map((b) => round2(b.cost)),
  );
  private seriesMargin = computed(() =>
    this.buckets().map((b) => round2(b.margin)),
  );

  // Totaux pour accent titre et légende
  totalCA = computed(() => this.buckets().reduce((s, b) => s + b.ca, 0));
  totalCost = computed(() => this.buckets().reduce((s, b) => s + b.cost, 0));
  totalCostMat = computed(() =>
    this.buckets().reduce((s, b) => s + b.costMat, 0),
  );
  totalCostMO = computed(() =>
    this.buckets().reduce((s, b) => s + b.costMO, 0),
  );
  totalMargin = computed(() => this.totalCA() - this.totalCost());
  totalMarginPct = computed(() => {
    const ca = this.totalCA();
    return ca > 0 ? this.totalMargin() / ca : 0;
  });

  accentLabel = computed(() => {
    const ca = this.totalCA();
    if (!ca) return null;
    return `${formatEuroShort(ca)} · ${(this.totalMarginPct() * 100).toFixed(1)} %`;
  });

  /** Classe de couleur du taux de marge global. */
  pctClass = computed(() => {
    const p = this.totalMarginPct() * 100;
    if (p < 5) return "on-error";
    if (p < 15) return "on-warning";
    if (p < 30) return "on-warning2";
    return "on-valid";
  });

  constructor() {
    effect(
      () => {
        if (this.canvasRef()) this.buildChart();
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (!this.chart || !this.canvasRef()) return;

        this.chart.data.labels = this.labels();

        const dsCA = this.chart.data.datasets[0] as ChartDataset<
          "bar",
          number[]
        >;
        const dsCost = this.chart.data.datasets[1] as ChartDataset<
          "bar",
          number[]
        >;
        const dsMargin = this.chart.data.datasets[2] as ChartDataset<
          "bar",
          number[]
        >;

        dsCA.data = this.seriesCA();
        dsCost.data = this.seriesCost();
        dsMargin.data = this.seriesMargin();

        this.chart.update("none");
      },
      { allowSignalWrites: true },
    );
  }

  private buildChart() {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;

    const styles = getComputedStyle(this.elRef.nativeElement);
    const bgAlt =
      styles.getPropertyValue("--color-on-background-alt").trim() || "#666";
    const gridColor = "rgba(128,128,128,0.12)";

    // Palette cohérente avec les autres widgets : CA = violet secondary,
    // Coût = orange/neutre, Marge = vert valid.
    const caColor = "#8b5cf6"; // CA
    const costColor = "#94a3b8"; // Coût
    const marginColor = "#10b981"; // Marge

    const config: ChartConfiguration<"bar", number[], string> = {
      type: "bar",
      data: {
        labels: this.labels(),
        datasets: [
          {
            label: "CA",
            data: this.seriesCA(),
            stack: "A",
            maxBarThickness: 28,
            backgroundColor: caColor,
            borderRadius: 4,
            borderColor: "transparent",
          },
          {
            label: "Coût",
            data: this.seriesCost(),
            stack: "B",
            maxBarThickness: 28,
            backgroundColor: costColor,
            borderRadius: 4,
            borderColor: "transparent",
          },
          {
            label: "Marge",
            data: this.seriesMargin(),
            stack: "B",
            maxBarThickness: 28,
            backgroundColor: marginColor,
            borderRadius: 4,
            borderColor: "transparent",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            stacked: false,
            grid: { display: false },
            ticks: {
              color: bgAlt,
              maxRotation: 0,
              autoSkip: true,
              font: { size: 10 },
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grace: "5%",
            grid: { color: gridColor },
            ticks: {
              color: bgAlt,
              font: { size: 10 },
              callback: (v: any) => formatEuroShort(parseNumber(v)),
            },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            padding: 10,
            titleFont: { size: 12, weight: "bold" },
            bodyFont: { size: 11 },
            callbacks: {
              title: (items) => items?.[0]?.label ?? "",
              label: (ctx) => {
                const b = this.buckets()[ctx.dataIndex ?? 0];
                const lbl = ctx.dataset?.label || "";
                const val = parseNumber(ctx.parsed?.y ?? 0);
                if (lbl === "CA") return ` CA: ${this.fmtEUR2.format(val)}`;
                if (lbl === "Coût")
                  return [
                    ` Coût: ${this.fmtEUR2.format(val)}`,
                    `   · mat. ${this.fmtEUR2.format(b?.costMat ?? 0)}`,
                    `   · mo.  ${this.fmtEUR2.format(b?.costMO ?? 0)}`,
                  ] as any;
                if (lbl === "Marge")
                  return ` Marge: ${this.fmtEUR2.format(val)} (${this.fmtPct1.format(
                    b?.marginPct ?? 0,
                  )})`;
                return ` ${lbl}: ${this.fmtEUR2.format(val)}`;
              },
            },
          },
        },
      },
    };

    this.chart?.destroy();
    this.chart = new Chart<"bar", number[], string>(ctx, config);
  }

  openFullList() {
    if (!this.buckets().length) return;
    this.dialogsService.open(
      CaParPeriodeDialogComponent,
      "750px",
      "fit-content",
      {
        title: "CA par période",
        tiersDenomination: this.stats()?.tiers?.denomination ?? "",
        buckets: this.buckets(),
        facturesLignes: this.stats()?.facturesLignes ?? [],
        lotCostMap: this.lotCostMap(),
        scale: this.timeScale(),
        articleFilterId: this.selectedArticleId() ?? null,
        articleFilterName: this.selectedArticleId()
          ? (this.articleInfosFacade
              .getByArticleId(this.selectedArticleId())
              ?.()?.denomination ?? null)
          : null,
        totalCA: this.totalCA(),
        totalCost: this.totalCost(),
        totalCostMat: this.totalCostMat(),
        totalCostMO: this.totalCostMO(),
        totalMargin: this.totalMargin(),
        totalMarginPct: this.totalMarginPct(),
      },
    );
  }

  ngOnDestroy() {
    this.chart?.destroy();
  }
}

/* ===== Helpers temps (exportés pour le dialog) ===== */

export function periodKey(d: Date, scale: CaPeriodeScale): string {
  return makeKey(startOf(d, scale), scale);
}

export function periodLabel(d: Date, scale: CaPeriodeScale): string {
  return formatKey(startOf(d, scale), scale);
}

function parseDate(v: any): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function startOf(d: Date, scale: CaPeriodeScale): Date {
  const dd = new Date(d);
  if (scale === "DAY") {
    dd.setHours(0, 0, 0, 0);
    return dd;
  }
  if (scale === "MONTH") {
    return new Date(dd.getFullYear(), dd.getMonth(), 1, 0, 0, 0, 0);
  }
  return startOfISOWeek(dd);
}

function addOne(d: Date, scale: CaPeriodeScale): Date {
  const dd = new Date(d);
  if (scale === "DAY") {
    dd.setDate(dd.getDate() + 1);
    return startOf(dd, "DAY");
  }
  if (scale === "MONTH") {
    return new Date(dd.getFullYear(), dd.getMonth() + 1, 1, 0, 0, 0, 0);
  }
  dd.setDate(dd.getDate() + 7);
  return startOfISOWeek(dd);
}

function startOfISOWeek(date: Date): Date {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - day);
  return d;
}

function isoWeek(date: Date): { year: number; week: number } {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week =
    1 +
    Math.round(((d.getTime() - firstThursday.getTime()) / 86400000 - 3) / 7);
  return { year: d.getUTCFullYear(), week };
}

function makeKey(d: Date, scale: CaPeriodeScale): string {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  if (scale === "DAY") return `${y}-${m}-${day}`;
  if (scale === "MONTH") return `${y}-${m}`;
  const { year, week } = isoWeek(d);
  return `${year}-W${week.toString().padStart(2, "0")}`;
}

function formatKey(d: Date, scale: CaPeriodeScale): string {
  if (scale === "DAY") {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
    }).format(d);
  }
  if (scale === "MONTH") {
    return new Intl.DateTimeFormat("fr-FR", {
      month: "2-digit",
      year: "numeric",
    }).format(d);
  }
  const { year, week } = isoWeek(d);
  return `S${week.toString().padStart(2, "0")} ${year}`;
}

function round2(n: number): number {
  return Math.round((n ?? 0) * 100) / 100;
}

function formatEuroShort(n: number): string {
  const v = Number(n ?? 0);
  if (!isFinite(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1_000_000)
    return `${(v / 1_000_000).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} M€`;
  if (abs >= 10_000)
    return `${(v / 1_000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} k€`;
  return `${v.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
}
